// ============================================================
//  src/pages/agent/AgentConsolePage.js
//  Lightweight, standalone inbox for a logged-in agent -- only
//  ever shows conversations assigned to THEM (enforced server-side
//  by api/agent/inbox/index.php). Reuses the owner-side Inbox CSS
//  for a consistent look without duplicating styles.
// ============================================================
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getAgentConversations, getAgentMessages, agentReply, agentAddNote, agentHandback, agentLogout,
  agentClearMessages, getAgentCannedResponses, getAgentLabels, agentSetLabels,
  getAgentContact, getAgentContactHistory, agentSetContactField,
  searchAgentMessages,
} from '../../api/client';
import '../bots/tabs/inbox.css';
import CannedResponseDropdown, { matchCannedTrigger, filterCannedResponses } from '../../components/common/CannedResponsePicker';
import { parseLabelIds, labelsForConversation, LabelChip, LabelPickerPopover } from '../../components/common/LabelPicker';
import ContactSidebar from '../../components/common/ContactSidebar';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  return Math.floor(diff / 86400) + 'd ago';
}

const AVATAR_PALETTE = ['#7c3aed', '#0ea5a4', '#db2777', '#2563eb', '#ea580c', '#16a34a', '#9333ea', '#0891b2'];
function avatarColor(str = '') {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

export default function AgentConsolePage() {
  const nav = useNavigate();
  const agent = JSON.parse(localStorage.getItem('agentInfo') || 'null');

  const [conversations, setConversations] = useState([]);
  const [selected, setSelected]           = useState(null); // chatId
  const [messages, setMessages]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [msgLoading, setMsgLoading]       = useState(false);
  const [replyText, setReplyText]         = useState('');
  const [replyMode, setReplyMode]         = useState('reply'); // 'reply' | 'note'
  const [sending, setSending]             = useState(false);
  const [mobileView, setMobileView]       = useState('list');
  const [hasMoreOlder, setHasMoreOlder]   = useState(false);
  const [loadingOlder, setLoadingOlder]   = useState(false);
  const [newMsgCount, setNewMsgCount]     = useState(0);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [cannedOpen, setCannedOpen]       = useState(false);
  const [cannedActiveIndex, setCannedActiveIndex] = useState(0);
  const [labels, setLabels]               = useState([]);
  const [labelPickerOpen, setLabelPickerOpen] = useState(false);
  const [isMobile, setIsMobile]           = useState(() => window.innerWidth <= 768);
  const [contactOpen, setContactOpen]     = useState(true);
  const [contact, setContact]             = useState(null);
  const [contactFields, setContactFields] = useState({});
  const [contactLoading, setContactLoading] = useState(false);
  const [contactEvents, setContactEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [convSearch, setConvSearch]       = useState('');
  const [msgSearchResults, setMsgSearchResults] = useState([]);
  const [msgSearchLoading, setMsgSearchLoading] = useState(false);
  const [highlightedMsgId, setHighlightedMsgId] = useState(null);
  const messagesEndRef                    = useRef(null);
  const messagesContainerRef              = useRef(null);
  const isNearBottomRef                   = useRef(true);
  const justSwitchedRef                   = useRef(false);
  const messagesRef                       = useRef([]);
  const isLoadingOlderRef                 = useRef(false);
  const pollRef                           = useRef(null);

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= 768); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const selectedConv = conversations.find(c => c.cChatId === selected) ?? null;

  const loadConversations = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await getAgentConversations('open');
      setConversations(res.data?.conversations ?? []);
    } catch { if (!silent) toast.error('Failed to load conversations'); }
    if (!silent) setLoading(false);
  }, []);

  const loadMessages = useCallback(async (chatId, silent = false, aroundId = null) => {
    if (!silent) setMsgLoading(true);
    try {
      const res = await getAgentMessages(chatId, aroundId ? { around: aroundId, limit: 50 } : { limit: 50 });
      setMessages(res.data?.messages ?? []);
      setHasMoreOlder(!!res.data?.has_more);
    } catch { /* silent */ }
    if (!silent) setMsgLoading(false);
  }, []);

  // Conversations narrowed by the search box (name/phone only, client-side)
  const filteredConversations = convSearch.trim()
    ? conversations.filter(c => {
        const q = convSearch.trim().toLowerCase();
        return (c.cName || '').toLowerCase().includes(q) || (c.cChatId || '').toLowerCase().includes(q);
      })
    : conversations;

  // Full-text search across message CONTENT, scoped to this agent's own
  // conversations server-side. Debounced the same way as the owner Inbox.
  useEffect(() => {
    const q = convSearch.trim();
    if (q.length < 2) { setMsgSearchResults([]); setMsgSearchLoading(false); return; }
    setMsgSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchAgentMessages(q);
        setMsgSearchResults(res.data?.results ?? []);
      } catch { setMsgSearchResults([]); }
      setMsgSearchLoading(false);
    }, 350);
    return () => clearTimeout(t);
  }, [convSearch]);

  // ── Load older messages — scrolling near the top, or the button ──
  const loadOlderMessages = useCallback(async (chatId) => {
    const current = messagesRef.current;
    if (isLoadingOlderRef.current || current.length === 0) return;
    const oldestId = current[0]?.mId;
    if (oldestId == null) return;

    isLoadingOlderRef.current = true;
    setLoadingOlder(true);
    const el = messagesContainerRef.current;
    const prevScrollHeight = el ? el.scrollHeight : 0;

    try {
      const res = await getAgentMessages(chatId, { before: oldestId, limit: 50 });
      const older = res.data?.messages ?? [];
      setHasMoreOlder(!!res.data?.has_more);
      if (older.length > 0) {
        setMessages(prev => [...older, ...prev]);
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevScrollHeight;
        });
      }
    } catch { /* silent */ }
    setLoadingOlder(false);
    isLoadingOlderRef.current = false;
  }, []);

  // ── Poll: only fetch what's new, append rather than replace ─────
  const pollNewMessages = useCallback(async (chatId) => {
    const current = messagesRef.current;
    const newestId = current.length ? current[current.length - 1].mId : null;
    if (newestId == null) return;
    try {
      const res = await getAgentMessages(chatId, { after: newestId });
      const fresh = res.data?.messages ?? [];
      if (fresh.length > 0) setMessages(prev => [...prev, ...fresh]);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // ── Load canned responses (shortcuts are managed by the bot owner
  //    in the main dashboard; agents only ever read the list here) ──
  useEffect(() => {
    getAgentCannedResponses()
      .then(res => setCannedResponses(res.data?.canned_responses ?? []))
      .catch(() => {});
  }, []);

  // ── Load labels (definitions are managed by the bot owner; agents
  //    only read the list and apply/remove them on their own chats) ──
  useEffect(() => {
    getAgentLabels()
      .then(res => setLabels(res.data?.labels ?? []))
      .catch(() => {});
  }, []);

  const labelsById = labels.reduce((acc, l) => { acc[l.lId] = l; return acc; }, {});

  // ── Toggle a label on/off the currently selected conversation ───
  async function toggleConversationLabel(lId) {
    if (!selected || !selectedConv) return;
    const current = parseLabelIds(selectedConv.cLabels);
    const next = current.includes(lId) ? current.filter(id => id !== lId) : [...current, lId];
    setConversations(prev => prev.map(c =>
      c.cChatId === selected ? { ...c, cLabels: next.join(',') } : c
    ));
    try {
      await agentSetLabels(selected, next);
    } catch {
      toast.error('Failed to update labels');
      loadConversations(true);
    }
  }

  useEffect(() => {
    pollRef.current = setInterval(() => {
      loadConversations(true);
      if (selected) pollNewMessages(selected);
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [loadConversations, pollNewMessages, selected]);

  // ── Scroll behaviour: only auto-scroll if already near the bottom.
  //    If the agent has scrolled up to read history, leave them there
  //    and just count unseen messages instead of yanking them back down. ──
  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    if (messages.length === 0) return;
    const added = messages.length - prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;

    if (isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: justSwitchedRef.current ? 'auto' : 'smooth' });
      justSwitchedRef.current = false;
      setNewMsgCount(0);
    } else if (added > 0) {
      setNewMsgCount(c => c + added);
    }
  }, [messages]);

  function handleMessagesScroll() {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nowNearBottom = distanceFromBottom < 80;
    isNearBottomRef.current = nowNearBottom;
    if (nowNearBottom) setNewMsgCount(0);

    if (el.scrollTop < 100 && hasMoreOlder && !isLoadingOlderRef.current && selected) {
      loadOlderMessages(selected);
    }
  }

  function jumpToBottom() {
    isNearBottomRef.current = true;
    setNewMsgCount(0);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  async function selectConversation(chatId) {
    setSelected(chatId);
    setMessages([]);
    setHasMoreOlder(false);
    isNearBottomRef.current = true;
    justSwitchedRef.current = true;
    prevMsgCountRef.current = 0;
    setNewMsgCount(0);
    setLabelPickerOpen(false);
    setMobileView('chat');
    await loadMessages(chatId);
    loadContact(chatId);
  }

  // ── Jump to a specific message from a full-text search result ───
  async function jumpToMessage(chatId, mId) {
    setSelected(chatId);
    setMessages([]);
    setHasMoreOlder(false);
    isNearBottomRef.current = false;
    justSwitchedRef.current = true;
    prevMsgCountRef.current = 0;
    setNewMsgCount(0);
    setLabelPickerOpen(false);
    setMobileView('chat');
    setConvSearch('');
    setMsgSearchResults([]);
    await loadMessages(chatId, false, mId);
    loadContact(chatId);
    setHighlightedMsgId(mId);
    requestAnimationFrame(() => {
      setTimeout(() => {
        const el = messagesContainerRef.current?.querySelector(`[data-msg-id="${mId}"]`);
        if (el) el.scrollIntoView({ block: 'center' });
      }, 60);
    });
    setTimeout(() => setHighlightedMsgId(null), 2200);
  }

  async function loadContact(chatId) {
    setContactLoading(true);
    setEventsLoading(true);
    try {
      const res = await getAgentContact(chatId);
      setContact(res.data?.contact ?? null);
      setContactFields(res.data?.fields ?? {});
    } catch { setContact(null); setContactFields({}); }
    setContactLoading(false);

    try {
      const res = await getAgentContactHistory(chatId);
      setContactEvents(res.data?.events ?? []);
    } catch { setContactEvents([]); }
    setEventsLoading(false);
  }

  async function handleSaveContactField(key, value) {
    if (!selected) return;
    try {
      await agentSetContactField(selected, key, value);
      setContactFields(f => ({ ...f, [key]: value }));
    } catch { toast.error('Failed to save'); }
  }

  function closeChat() {
    setSelected(null);
    setMessages([]);
    setMobileView('list');
    setNewMsgCount(0);
  }

  async function handleReply(e) {
    e.preventDefault();
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      if (replyMode === 'note') {
        await agentAddNote(selected, replyText.trim());
      } else {
        await agentReply(selected, replyText.trim());
      }
      setReplyText('');
      isNearBottomRef.current = true;
      await pollNewMessages(selected);
      await loadConversations(true);
    } catch { toast.error(replyMode === 'note' ? 'Failed to add note' : 'Failed to send reply'); }
    setSending(false);
  }

  async function handleClearMessages() {
    if (!selected) return;
    if (!window.confirm(
      "Clear this chat? This only clears the message history here — it never touches WhatsApp. " +
      "The customer keeps their own copy on their phone and won't see anything happen. " +
      "New messages will keep coming in normally right after this."
    )) return;
    try {
      await agentClearMessages(selected);
      toast.success('Chat cleared on your end');
      setMessages([]);
      setHasMoreOlder(false);
      await loadConversations(true);
    } catch { toast.error('Failed to clear messages'); }
  }

  async function handleHandback() {
    if (!window.confirm('Hand this conversation back to the bot?')) return;
    try {
      await agentHandback(selected);
      toast.success('Handed back to bot');
      closeChat();
      await loadConversations();
    } catch { toast.error('Failed to hand back'); }
  }

  // ── Canned response "/" autocomplete ─────────────────────────
  const cannedQuery = matchCannedTrigger(replyText);
  const cannedMatches = cannedQuery !== null ? filterCannedResponses(cannedResponses, cannedQuery) : [];

  function handleReplyTextChange(val) {
    setReplyText(val);
    const q = matchCannedTrigger(val);
    if (replyMode === 'reply' && q !== null && cannedResponses.length > 0) {
      setCannedOpen(true);
      setCannedActiveIndex(0);
    } else {
      setCannedOpen(false);
    }
  }

  function pickCanned(c) {
    setReplyText(c.crBody);
    setCannedOpen(false);
  }

  function handleReplyKeyDown(e) {
    if (cannedOpen && cannedMatches.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setCannedActiveIndex(i => (i + 1) % cannedMatches.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setCannedActiveIndex(i => (i - 1 + cannedMatches.length) % cannedMatches.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickCanned(cannedMatches[cannedActiveIndex]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setCannedOpen(false); return; }
    }
  }

  async function handleLogout() {
    try { await agentLogout(); } catch { /* ignore */ }
    localStorage.removeItem('agentAuthToken');
    localStorage.removeItem('agentInfo');
    nav('/agent', { replace: true });
  }

  return (
    <div
      className={`inbox-wrap mobile-view-${mobileView}`}
      style={{ height: '100vh', display: 'flex', minHeight: 0, overflow: 'hidden', boxSizing: 'border-box' }}
    >
      {/* ── Sidebar ── */}
      <aside
        className="inbox-sidebar"
        style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden', boxSizing: 'border-box' }}
      >
        <div className="inbox-sidebar-header">
          <div className="inbox-header-row">
            <div>
              <div className="inbox-header-title">My conversations</div>
              <div className="inbox-header-sub">
                {agent?.aName} · {agent?.botName}
              </div>
            </div>
            <button className="btn btn-sm btn-outline" onClick={handleLogout} title="Log out">
              <i className="fas fa-right-from-bracket" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="search-bar inbox-search-bar">
          <span className="search-icon">🔍</span>
          <input
            className="search-input"
            placeholder="Search by name, phone, or message…"
            value={convSearch}
            onChange={e => setConvSearch(e.target.value)}
          />
        </div>

        {/* Full-text message search results */}
        {convSearch.trim().length >= 2 && (
          <div style={{ borderBottom: '1.5px solid #e5e7eb', flexShrink: 0 }}>
            <div style={{ padding: '8px 14px 4px', fontSize: 10.5, fontWeight: 700, color: '#8a94a3', textTransform: 'uppercase', letterSpacing: 0.3 }}>
              Message matches {msgSearchLoading && '…'}
            </div>
            {!msgSearchLoading && msgSearchResults.length === 0 ? (
              <div style={{ padding: '2px 14px 12px', fontSize: 12, color: '#9ca3af' }}>
                No messages contain "{convSearch.trim()}"
              </div>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: '0 0 6px', maxHeight: 220, overflowY: 'auto' }}>
                {msgSearchResults.map(r => (
                  <li key={r.chatId}>
                    {r.matches.map(m => (
                      <button
                        key={m.mId}
                        type="button"
                        onClick={() => jumpToMessage(r.chatId, m.mId)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '7px 14px', border: 'none', background: 'transparent',
                          cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box',
                          WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>
                          {r.cName || r.chatId}
                          {r.matchCount > 1 && (
                            <span style={{ fontWeight: 600, color: '#9ca3af', fontSize: 10.5 }}> · {r.matchCount} matches</span>
                          )}
                        </div>
                        <div style={{
                          fontSize: 11.5, color: '#6b7280', marginTop: 1,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {m.mText}
                        </div>
                      </button>
                    ))}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {loading ? (
          <div className="inbox-empty">Loading…</div>
        ) : filteredConversations.length === 0 ? (
          <div className="inbox-empty">
            {convSearch.trim() ? 'No matches' : 'No conversations assigned to you yet.'}
          </div>
        ) : (
          <ul className="inbox-conv-list" style={{ flex: 1, minHeight: 0, overflowY: 'auto', listStyle: 'none', margin: 0, padding: 0 }}>
            {filteredConversations.map(c => {
              const convLabels = labelsForConversation(c, labelsById);
              return (
                <li
                  key={c.cChatId}
                  className={`inbox-conv-item ${selected === c.cChatId ? 'active' : ''}`}
                  onClick={() => selectConversation(c.cChatId)}
                >
                  <div className="conv-avatar" style={{ background: avatarColor(c.cName || c.cChatId) }}>
                    {(c.cName || c.cChatId || '?')[0].toUpperCase()}
                  </div>
                  <div className="conv-meta">
                    <div className="conv-top">
                      <span className="conv-name">{c.cName || c.cChatId}</span>
                      <span className="conv-time">{timeAgo(c.cLastMsgAt)}</span>
                    </div>
                    <div className="conv-bottom">
                      <span className="conv-preview">{c.cLastMsg || '—'}</span>
                    </div>
                    {convLabels.length > 0 && (
                      <div className="conv-labels">
                        {convLabels.map(l => <LabelChip key={l.lId} label={l} small />)}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </aside>

      {/* ── Chat ── */}
      <main
        className="inbox-chat"
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0, height: '100%', overflow: 'hidden', boxSizing: 'border-box' }}
      >
        {!selectedConv ? (
          <div className="inbox-placeholder">Select a conversation to view it here.</div>
        ) : (
          <>
            {/* Chat header */}
            <div style={{ flexShrink: 0, borderBottom: '1.5px solid #e5e7eb', background: '#fff', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' }}>
                <button className="icon-btn mobile-back" onClick={closeChat}>
                  <i className="fas fa-arrow-left" />
                </button>
                <div className="conv-avatar" style={{ background: avatarColor(selectedConv.cName || selectedConv.cChatId), flexShrink: 0 }}>
                  {(selectedConv.cName || selectedConv.cChatId || '?')[0].toUpperCase()}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedConv.cName || selectedConv.cChatId}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#8a94a3' }}>{selectedConv.cChatId}</div>
                </div>
                <button
                  type="button"
                  onClick={() => isMobile ? setMobileView('contact') : setContactOpen(o => !o)}
                  title="Contact details"
                  aria-label="Toggle contact details"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, minWidth: 28, borderRadius: '50%',
                    border: `1.5px solid ${contactOpen && !isMobile ? '#00C88C' : '#e5e7eb'}`,
                    background: contactOpen && !isMobile ? '#e8f9f3' : '#fff',
                    color: contactOpen && !isMobile ? '#00966b' : '#6b7280',
                    fontSize: 12, cursor: 'pointer', flexShrink: 0, boxSizing: 'border-box',
                    WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                  }}
                >
                  ⓘ
                </button>
              </div>

              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                padding: '9px 14px',
                background: '#f9fafb',
                borderTop: '1px solid #f0f1f3',
                boxSizing: 'border-box',
              }}>
                {labelsForConversation(selectedConv, labelsById).map(l => (
                  <LabelChip key={l.lId} label={l} onRemove={() => toggleConversationLabel(l.lId)} />
                ))}
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setLabelPickerOpen(o => !o)}
                    style={{
                      padding: '3px 10px',
                      borderRadius: 999,
                      border: '1.5px dashed #cbd5e1',
                      background: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      appearance: 'none',
                    }}
                  >
                    + Label
                  </button>
                  {labelPickerOpen && (
                    <LabelPickerPopover
                      labels={labels}
                      selectedIds={parseLabelIds(selectedConv?.cLabels)}
                      onToggle={toggleConversationLabel}
                      onClose={() => setLabelPickerOpen(false)}
                    />
                  )}
                </div>

                <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                  <button
                    type="button"
                    onClick={handleHandback}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '5px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb',
                      background: '#fff', color: '#374151', fontSize: 11.5, fontWeight: 700,
                      cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box',
                      WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                    }}
                  >
                    <i className="fas fa-rotate-left" /> Hand back to bot
                  </button>
                  <button
                    type="button"
                    onClick={handleClearMessages}
                    title="Clear chat history (your side only)"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '5px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb',
                      background: '#fff', color: '#374151', fontSize: 11.5, fontWeight: 700,
                      cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box',
                      WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                    }}
                  >
                    <i className="fas fa-broom" /> Clear
                  </button>
                </div>
              </div>
            </div>

            <div
              className="inbox-messages"
              ref={messagesContainerRef}
              onScroll={handleMessagesScroll}
              style={{ flex: 1, minHeight: 0, overflowY: 'auto', boxSizing: 'border-box' }}
            >
              {loadingOlder && (
                <div className="inbox-loading-older">Loading earlier messages…</div>
              )}
              {!msgLoading && !loadingOlder && hasMoreOlder && messages.length > 0 && (
                <button
                  type="button"
                  className="inbox-load-older-btn"
                  onClick={() => loadOlderMessages(selected)}
                >
                  ↑ Load earlier messages
                </button>
              )}
              {msgLoading ? (
                <div className="inbox-empty">Loading messages…</div>
              ) : (
                messages.map(m => (
                  <div key={m.mId} className={`msg-bubble-wrap ${m.mDirection === 'out' ? 'out' : 'in'}`} data-msg-id={m.mId}>
                    <div
                      className={`msg-bubble ${m.mSenderType === 'agent' ? 'agent-bubble' : ''} ${m.mSenderType === 'note' ? 'note-bubble' : ''}`}
                      style={{
                        ...(m.mSenderType === 'note' ? { background: '#fef3c7', color: '#713f12', border: '1.5px dashed #d97706' } : {}),
                        ...(m.mId === highlightedMsgId ? {
                          boxShadow: '0 0 0 3px #fbbf24, 0 0 0 6px rgba(251,191,36,0.35)',
                          transition: 'box-shadow 0.3s ease',
                        } : {}),
                      }}
                    >
                      {m.mSenderType === 'note' && (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '1px 8px',
                          borderRadius: 999,
                          background: '#d97706',
                          color: '#fff',
                          fontSize: 9.5,
                          fontWeight: 800,
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                          marginBottom: 4,
                          boxSizing: 'border-box',
                        }}>
                          📝 Note{m.agentName ? ` · ${m.agentName}` : ''} · team only
                        </div>
                      )}
                      {m.mSenderType === 'agent' && m.mSenderType !== 'note' && (
                        <div className="msg-agent-label">🧑 {m.agentName || 'Agent'}</div>
                      )}
                      {m.mText && <p className="msg-text">{m.mText}</p>}
                      <span className="msg-time">
                        {new Date(m.mCreatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {newMsgCount > 0 && (
              <button type="button" className="inbox-jump-btn" onClick={jumpToBottom}>
                ↓ {newMsgCount} new message{newMsgCount > 1 ? 's' : ''}
              </button>
            )}

            {/* Composer — toggle + reply/note box, always pinned to the bottom */}
            <div style={{ flexShrink: 0, borderTop: '1.5px solid #e5e7eb', background: '#fff', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', gap: 6, padding: '10px 14px 0', boxSizing: 'border-box' }}>
                <button
                  type="button"
                  onClick={() => setReplyMode('reply')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', borderRadius: 8, border: 'none',
                    background: replyMode === 'reply' ? '#e8f9f3' : 'transparent',
                    color: replyMode === 'reply' ? '#00966b' : '#8a94a3',
                    fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    boxSizing: 'border-box', WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                  }}
                >
                  <span style={{ fontSize: 13 }}>↩</span> Reply
                </button>
                <button
                  type="button"
                  onClick={() => { setReplyMode('note'); setCannedOpen(false); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', borderRadius: 8, border: 'none',
                    background: replyMode === 'note' ? '#fef3c7' : 'transparent',
                    color: replyMode === 'note' ? '#92400e' : '#8a94a3',
                    fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    boxSizing: 'border-box', WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                  }}
                >
                  <span style={{ fontSize: 13 }}>📝</span> Note
                </button>
              </div>

              <form
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px 14px',
                  boxSizing: 'border-box',
                  background: replyMode === 'note' ? '#fffbeb' : 'transparent',
                }}
                onSubmit={handleReply}
              >
                {cannedOpen && replyMode === 'reply' && (
                  <CannedResponseDropdown
                    items={cannedMatches}
                    activeIndex={cannedActiveIndex}
                    onPick={pickCanned}
                    onHoverIndex={setCannedActiveIndex}
                  />
                )}
                <input
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: `1.5px solid ${replyMode === 'note' ? '#fcd34d' : '#e5e7eb'}`,
                    borderRadius: 999,
                    padding: '10px 16px',
                    fontSize: 13.5,
                    fontFamily: 'inherit',
                    color: '#1f2937',
                    background: '#fff',
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                  value={replyText}
                  onChange={e => handleReplyTextChange(e.target.value)}
                  onKeyDown={handleReplyKeyDown}
                  placeholder={replyMode === 'note'
                    ? 'Leave a private note — only your team can see this…'
                    : 'Type a reply… (type / for saved replies)'}
                  disabled={sending}
                />
                <button
                  type="submit"
                  aria-label={replyMode === 'note' ? 'Add note' : 'Send message'}
                  title={replyMode === 'note' ? 'Add note' : 'Send message'}
                  disabled={sending || !replyText.trim()}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 40, height: 40, minWidth: 40, borderRadius: '50%', border: 'none',
                    background: !replyText.trim() ? '#d1d5db' : (replyMode === 'note' ? '#d97706' : '#00C88C'),
                    color: '#fff', fontSize: 16, cursor: (sending || !replyText.trim()) ? 'default' : 'pointer',
                    flexShrink: 0, boxSizing: 'border-box',
                    WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                    transition: 'background 0.12s',
                  }}
                >
                  {sending ? <span className="spinner" /> : replyMode === 'note' ? <i className="fas fa-note-sticky" /> : <i className="fas fa-paper-plane" style={{ marginLeft: -1 }} />}
                </button>
              </form>
            </div>
          </>
        )}
      </main>

      {/* ── Contact Profile Sidebar (3rd column) ──────────────── */}
      {selected && (isMobile ? mobileView === 'contact' : contactOpen) && (
        <ContactSidebar
          loading={contactLoading}
          contact={contact}
          fields={contactFields}
          events={contactEvents}
          eventsLoading={eventsLoading}
          chatId={selected}
          convName={selectedConv?.cName}
          convLabels={selectedConv?.cLabels}
          allLabels={labels}
          onToggleLabel={toggleConversationLabel}
          onSaveField={handleSaveContactField}
          onClose={isMobile ? () => setMobileView('chat') : () => setContactOpen(false)}
          fullWidth={isMobile}
        />
      )}
    </div>
  );
}